import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SECONDS, STOCKS_BOOST_ACTIVATION } from '@constants/miscellaneous';
import { IGeneralConstants } from '@interfaces/general-constants';
import { IUser } from '@interfaces/users';
import { ModalController, Platform } from '@ionic/angular';
import { AuthService } from '@services/auth/auth.service';
import { ErrorAndLoadingService } from '@services/error-loading/error-loading.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-security-lending-activation-modal',
  templateUrl: './modal-security-lending-activation.page.html',
  styleUrls: ['./modal-security-lending-activation.page.scss'],
})
export class ModalSecurityLendingActivationPage implements OnInit, OnDestroy {
  @Input() title: any;
  @Input() details: any;
  @Input() userInfo: IUser;
  @Input() generalConstants: IGeneralConstants;
  userHasCompleteInfo: boolean;
  agreementUrl: string;
  articleUrl: string;
  isMobile: boolean;
  userInfoSubscription: Subscription;
  maxRetry = 3;
  retryCount = 1;
  showShadow = true;
  disclosureUrl: string;

  constructor(
    private modalCtrl: ModalController,
    private navigationService: NavigationService,
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
    private platform: Platform,
    private authService: AuthService,
  ) { }

  ngOnInit() {
    this.agreementUrl = this.generalConstants.securityLending.agreementUrl;
    this.articleUrl = this.generalConstants.securityLending.articleUrl;
    this.disclosureUrl = this.generalConstants.securityLending.disclosureUrl;
    this.userHasCompleteInfo = (this.userInfo.infoComplete || this.userInfo.temporaryInfoComplete);
    this.isMobile = this.platform.is('ios') || this.platform.is('android');
  }

  ngOnDestroy() {
    this.userInfoSubscription && this.userInfoSubscription.unsubscribe();
  }

  dismiss() {
    this.firebaseService.logEvent('closed_stocks_boost_modal', {}, true);
    this.modalCounter();
    this.modalCtrl.dismiss();
  }

  async modalCounter() {
    try {
      const notificationsCollection = this.firebaseService.getCollection(`users/${this.userInfo.uid}/notifications`);
      const securityLendingModalDoc = await this.firebaseService.getDocument('securityLendingModal', notificationsCollection);
      const newData = {
        count: securityLendingModalDoc ? securityLendingModalDoc.count + 1 : 1,
      };
      if (securityLendingModalDoc) {
        this.firebaseService.updateDocument(newData, notificationsCollection, 'securityLendingModal');
      } else {
        this.firebaseService.addDocumentWithID(newData, notificationsCollection, 'securityLendingModal');
      }
    } catch (error) {
      this.firebaseService.logError(error);
    }
  }

  async goToAgreement() {
    this.navigationService.openInBrowser(this.agreementUrl);
  }

  async goToArticle() {
    this.navigationService.openInBrowser(this.articleUrl);
    this.firebaseService.logEvent('clicked_link_more_about_boost', {});
  }

  async goToAssociatedRisks() {
    this.navigationService.openInBrowser(this.disclosureUrl);
  }

  async acceptStocksBoost() {
    await this.errorService.showLoading();
    try {
      const response = await this.firebaseService.callableFunction('DrivewealthUserUpdateFpslAgreement');
      if (response === 'ERR') {
        throw response;
      }
      this.confirmStocksBoostActivation();
    } catch (error) {
      this.firebaseService.logError(JSON.stringify(error));
      this.errorService.hideLoading();
      this.errorService.presentToastWithDuration(
        6 * SECONDS,
        'top',
        'No se pudo activar Stocks Boost. Intenta activarlo en la sección Inversiones de tu Perfil.',
        false,
      );
      this.modalCtrl.dismiss({ success: false });
    }
  }

  confirmStocksBoostActivation() {
    this.userInfoSubscription = this.authService.currentUserSource.subscribe(
      userInfo => {
        if (this.userInfoSubscription) {
          this.userInfoSubscription.unsubscribe();
          this.userInfoSubscription = null;
        }
        this.userInfo = userInfo;
        if (this.userInfo.requiredChecks.fpslAgreement.accepted) {
          this.errorService.hideLoading();
          this.errorService.presentToastWithDuration(
            3 * SECONDS,
            'top',
            STOCKS_BOOST_ACTIVATION.SUCCESS,
            false,
          );
          this.modalCtrl.dismiss({ success: true });
        }
      },
      error => {
        if (this.userInfoSubscription) {
          this.userInfoSubscription.unsubscribe();
          this.userInfoSubscription = null;
        }
        this.firebaseService.logError(JSON.stringify(error));
        if (this.retryCount < this.maxRetry) {
          this.retryCount++;
          return setTimeout(() => this.confirmStocksBoostActivation(), 400);
        }
        this.errorService.hideLoading();
        this.modalCtrl.dismiss();
      },
    );
  }

  scrolledContent(event) {
    setTimeout(() => {
      this.showShadow = event.detail.scrollTop < 160;
    }, 300);
  }
}
